'use client';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getBasket, setLastPaymentType } from '@/lib/BasketStorage';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { getBasketEcommerceBody, GTMEventType } from '@/lib/GTMEventType';
import { Requester } from '@/lib/Requester';
import { WebLink } from '@/lib/WebLinks';
import { BankAccount } from '@/models/BankAccount';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import PrivacyDialog from './PrivacyDialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Spinner } from './ui/spinner';
import { Textarea } from './ui/textarea';
import { useToast } from './ui/use-toast';

export function CheckoutPaymentTypeBankTransfer({
  paymentType,
  orderNumber,
  accounts,
}: {
  paymentType: number;
  orderNumber: string;
  accounts: BankAccount[];
}) {
  const router = useRouter();
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | undefined>(accounts[0]);
  const defaultMessage = `${orderNumber} numaralı siparişimin ödemesi, seçili banka hesabınıza gönderilmiştir.`;
  const [isLoading, setIsLoading] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isPrivacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  async function startOrder() {
    try {
      if (isLoading) {
        return;
      }

      if (!privacyChecked) {
        toast({
          variant: 'destructive',
          description: 'Gizlilik sözleşmesini kabul etmelisiniz',
        });
        return;
      }

      if (!selectedAccount) {
        toast({
          variant: 'destructive',
          description: 'Havale/EFT yaptığınız bankayı seçiniz',
        });
        return;
      }
      setIsLoading(true);
      const paymentMessage = message === '' ? defaultMessage : message;
      const body = {
        paymentType,
        paymentInformation: {
          bankAccountId: selectedAccount.id,
          paymentMessage,
          orderNumber,
        },
      };
      const basket = getBasket();
      setLastPaymentType('Havale');

      if (basket) {
        sendGTMEvent({
          event: GTMEventType.ADD_PAYMENT_INFO,
          ecommerce: {
            ...getBasketEcommerceBody(basket),
            payment_type: 'Havale',
          },
        });
      }
      await Requester.post(Endpoints.START_ORDER, body);
      router.replace(
        orderNumber
          ? `${WebLink.ORDER_RECEIVED}?on=${encodeURIComponent(orderNumber)}`
          : WebLink.ORDER_RECEIVED,
      );
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 sm:gap-8 justify-between items-start pt-6">
      <div className="w-full md:w-1/2">
        <h2 className="text-[24px] font-normal mb-4 border-b-2 border-hzc_beige_600">
          Banka Bilgileri
        </h2>
        {accounts.map((account) => (
          <BankAccountView key={account.id} account={account} />
        ))}
      </div>
      <div className="w-full md:w-1/2">
        <h2 className="text-[24px] font-normal mb-4 border-b-2 border-hzc_beige_600">
          Sipariş No: {orderNumber}
        </h2>
        <p className="mb-4 text-[14px] font-normal">
          Havale - EFT sırasında açıklama kısmına sipariş numarasını yazmanız gerekmektedir.
        </p>
        <p className="mb-4 text-[14px] font-normal">
          Havale - EFT yapmadan önce{' '}
          <strong className="text-[#DC143C]">&quot;Siparişi Tamamla&quot; butonuna</strong> basmayı
          unutmayınız. Aksi halde siparişiniz tarafımıza iletilmez.
        </p>
        <p className="mb-4 text-[14px] font-normal">
          <strong className="text-[#DC143C]">{orderNumber}</strong> sipariş numarasıyla
          siparişinizin durumunu takip edebilirsiniz. Ödeme yaptıktan sonra siparişiniz ödeme alındı
          durumuna geçmezse lütfen bizimle iletişime geçin.
        </p>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300 w-full h-[48px] text-hzc_beige_100 rounded-full"
              onClick={() => setDialogOpen(true)}
            >
              Siparişi Tamamla
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="text-[44px] text-hzc_purple_500">Ödeme Bildirimi</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Select
                value={selectedAccount?.id?.toString()}
                onValueChange={(value) => {
                  setSelectedAccount(accounts.find((acc) => acc.id.toString() === value));
                }}
              >
                <SelectTrigger className="h-[48px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm mt-2">
                  <SelectValue placeholder="Banka Seçiniz" />
                </SelectTrigger>
                <SelectContent className="text-[12px] font-medium">
                  <SelectGroup>
                    {accounts.map((acc) => (
                      <SelectItem
                        className="text-[12px] hover:cursor-pointer"
                        value={acc.id.toString()}
                        key={acc.id}
                      >
                        {acc.bankName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <Textarea
                placeholder={defaultMessage}
                className="max-h-[128px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm mt-2"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className="flex items-center gap-2">
                <Checkbox
                  className="w-4 h-4 flex-shrink-0 "
                  checked={privacyChecked}
                  onCheckedChange={(isChecked) => setPrivacyChecked(isChecked === true)}
                />
                <span className="text-sm text-hzc_beige_700">
                  <span
                    className="font-medium hover:underline hover:cursor-pointer"
                    onClick={() => setPrivacyDialogOpen(true)}
                  >
                    Satış Sözleşmesi
                  </span>
                  &apos;ni okudum ve kabul ediyorum. *
                </span>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                className="text-hzc_purple_500 bg-[#5E6AFF1A] hover:bg-[#5E6AFF1A] transition-all duration-300 hover:scale-105 rounded-full h-[48px]"
                onClick={() => setDialogOpen(false)}
              >
                Kapat
              </Button>
              <Button
                className="bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300 h-[48px] text-hzc_beige_100 rounded-full gap-2"
                onClick={() => startOrder()}
              >
                {isLoading && <Spinner className="size-6 text-white" />}
                Gönder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <PrivacyDialog open={isPrivacyDialogOpen} onOpenChange={setPrivacyDialogOpen} />
      </div>
    </div>
  );
}

function BankAccountView({ account }: { account: BankAccount }) {
  return (
    <div className="w-full flex flex-col">
      <div className="mb-4">
        <span className="font-semibold text-[18px]">IBAN:</span>
        <p className="text-[14px] font-normal">{account.iban}</p>
      </div>
      <div className="mb-4">
        <span className="font-semibold text-[18px]">Hesap Sahibi:</span>
        <p className="text-[14px] font-normal">{account.ownerName}</p>
      </div>
      <div className="mb-4">
        <span className="font-semibold text-[18px]">Şube:</span>
        <p className="text-[14px] font-normal">{account.branchName}</p>
      </div>
      <div className="mb-4">
        <span className="font-semibold text-[18px]">Şube Kodu:</span>
        <p className="text-[14px] font-normal">{account.branchCode}</p>
      </div>
      <div className="mb-4">
        <span className="font-semibold text-[18px]">Hesap No:</span>
        <p className="text-[14px] font-normal">{account.accountNumber}</p>
      </div>
    </div>
  );
}
